import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { InvoiceMoneyBar } from '@/components/sales/invoice-money-bar';
import { SalesTable } from '@/components/sales/sales-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import {
  FILTERS,
  einvoiceFilterLabel,
  matchesFilter,
  useEInvoiceMap,
  type EInvoiceFilter,
} from '@/lib/einvoicing';
import { PERIODS, periodStart } from '@/lib/dates';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useSalesTransactions, type SalesRow } from '@/lib/sales';
import { cn } from '@/lib/utils';

type Chip = 'all' | 'draft' | 'unpaid' | 'overdue' | 'not_due' | 'partially_paid' | 'paid' | 'void';
const CHIPS = [
  ['all', 'salesPages.list.all'],
  ['draft', 'salesPages.status.draft'],
  ['unpaid', 'salesPages.status.unpaid'],
  ['overdue', 'salesPages.status.overdue'],
  ['not_due', 'salesPages.invoiceList.notDue'],
  ['partially_paid', 'salesPages.status.partiallyPaid'],
  ['paid', 'salesPages.status.paid'],
  ['void', 'salesPages.status.void'],
] as const satisfies ReadonlyArray<readonly [Chip, string]>;
const open = (r: SalesRow) => r.status === 'approved' || r.status === 'partially_paid';
const MATCH: Record<Chip, (r: SalesRow) => boolean> = {
  all: () => true,
  draft: (r) => r.status === 'draft',
  unpaid: open,
  overdue: (r) => open(r) && r.overdue,
  not_due: (r) => open(r) && !r.overdue,
  partially_paid: (r) => r.status === 'partially_paid',
  paid: (r) => r.status === 'paid',
  void: (r) => r.status === 'void',
};

export const Route = createFileRoute('/sales/invoices/')({
  component: Invoices,
  validateSearch: (s: Record<string, unknown>): { status?: Chip } =>
    typeof s['status'] === 'string' && s['status'] in MATCH ? { status: s['status'] as Chip } : {},
});

/** Invoices list with money bar and status chips (document 06 S3). */
function Invoices() {
  const t = useT();
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const { status = 'all' } = Route.useSearch() as { status?: Chip };
  const [q, setQ] = useState('');
  const [period, setPeriod] = useState('all');
  const [einvoice, setEInvoice] = useState<EInvoiceFilter>('all');
  const einvoices = useEInvoiceMap();
  const tx = useSalesTransactions();
  const needle = q.trim().toLowerCase();
  const since = periodStart(period);
  const rows = (tx.data ?? []).filter(
    (r) =>
      r.type === 'invoice' &&
      MATCH[status](r) &&
      (!since || r.date >= since) &&
      (!einvoices ||
        einvoice === 'all' ||
        (einvoices.has(r.id) && matchesFilter(einvoices.get(r.id)!, einvoice))) &&
      (!needle ||
        r.contactName.toLowerCase().includes(needle) ||
        (r.number ?? '').toLowerCase().includes(needle) ||
        (r.memo ?? '').toLowerCase().includes(needle)),
  );
  const setStatus = (s: Chip) =>
    void navigate({ to: '/sales/invoices', search: s === 'all' ? {} : { status: s } });
  return (
    <>
      <PageHeader
        title={t('salesPages.invoiceList.title')}
        actions={
          me.data?.isStaff ? (
            <Button asChild>
              <Link to="/sales/invoices/new">{t('salesPages.salesListCommon.newInvoice')}</Link>
            </Button>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : (
        <>
          <InvoiceMoneyBar
            active={status === 'overdue' || status === 'not_due' ? status : null}
            onSelect={(k) => setStatus((k as Chip | null) ?? 'all')}
          />
          <div
            className="mb-3 flex flex-wrap items-center gap-2"
            role="tablist"
            aria-label={t('salesPages.salesListCommon.statusLabel')}
          >
            {CHIPS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={status === k}
                onClick={() => setStatus(k)}
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  status === k
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'bg-card text-muted-foreground hover:bg-muted',
                )}
              >
                {t(label)}
              </button>
            ))}
          </div>
          <div className="mb-3 flex items-center gap-4">
            <div className="relative w-[340px]">
              <Search className="pointer-events-none absolute start-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card ps-8"
                placeholder={t('salesPages.invoiceList.searchPlaceholder')}
                aria-label={t('salesPages.invoiceList.searchLabel')}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <NativeSelect
              aria-label={t('salesPages.salesListCommon.dateLabel')}
              className="w-[200px] bg-card"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIODS.map(([v]) => (
                <option key={v} value={v}>
                  {t(`purchasePages.listCommon.dateFilter.${v as '30'}`)}
                </option>
              ))}
            </NativeSelect>
            {einvoices ? (
              <NativeSelect
                aria-label={t('salesPages.invoiceList.einvoiceLabel')}
                className="w-[200px] bg-card"
                value={einvoice}
                onChange={(e) => setEInvoice(e.target.value as EInvoiceFilter)}
              >
                {[
                  ...FILTERS.filter(([v]) => v === 'all'),
                  ...FILTERS.filter(([v]) => v !== 'all'),
                ].map(([v]) => (
                  <option key={v} value={v}>
                    {t('salesPages.invoiceList.einvoiceFilter', { filter: einvoiceFilterLabel(v) })}
                  </option>
                ))}
              </NativeSelect>
            ) : null}
          </div>
          {tx.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : tx.isError ? (
            <EmptyState title={t('salesPages.invoiceList.loadError')} hint={tx.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState
              title={
                (tx.data ?? []).some((r) => r.type === 'invoice')
                  ? t('salesPages.invoiceList.noMatch')
                  : t('salesPages.invoiceList.empty')
              }
              action={
                me.data?.isStaff ? (
                  <Button asChild>
                    <Link to="/sales/invoices/new">
                      {t('salesPages.salesListCommon.newInvoice')}
                    </Link>
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <SalesTable rows={rows} showType={false} einvoices={einvoices} batch />
          )}
        </>
      )}
    </>
  );
}
